import React, { useState } from "react";
import { X, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";

export default function AuthModal() {
  const { authOpen, setAuthOpen, login, signup, isFanJoin } = useApp();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (authOpen && isFanJoin) setMode("signup");
  }, [authOpen, isFanJoin]);

  if (!authOpen) return null;

  const reset = () => {
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    if (mode === "signup" && password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    const res =
      mode === "login"
        ? await login(email.trim(), password)
        : await signup(name.trim(), email.trim(), password, isFanJoin ? "fan" : "artist");
    setBusy(false);
    if (res.ok) {
      setAuthOpen(false);
      setName(""); setEmail(""); setPassword("");
    } else {
      setError(res.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setAuthOpen(false)} />
      <div className="relative z-10 w-full max-w-[400px] rounded-3xl bg-[#101012] border border-white/10 p-6">
        <button onClick={() => setAuthOpen(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10">
          <X size={16} />
        </button>

        <div className="flex justify-center mb-1">
          <img src="/brand/s71-logo-sm.png" alt="S71" className="h-12 w-auto object-contain" />
        </div>
        <p className="text-center text-[13px] text-white/45 mb-5">
          {isFanJoin
            ? (mode === "login" ? "Sign in to access exclusive content" : "Join as a fan — unlock exclusive content")
            : (mode === "login" ? "Sign in to unlock exclusive content" : "Create your free account")}
        </p>

        <div className="flex p-1 rounded-full bg-white/5 mb-5">
          {["login", "signup"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); reset(); }}
              className={`flex-1 h-9 rounded-full text-[13px] font-medium transition-colors ${
                mode === m ? "bg-white text-black" : "text-white/60"
              }`}
            >
              {m === "login" ? "Sign In" : (isFanJoin ? "Fan Sign Up" : "Sign Up")}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <div className="flex items-center gap-2 px-3 h-11 rounded-xl bg-white/[0.05] border border-white/10">
              <UserIcon size={16} className="text-white/40" />
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35" />
            </div>
          )}
          <div className="flex items-center gap-2 px-3 h-11 rounded-xl bg-white/[0.05] border border-white/10">
            <Mail size={16} className="text-white/40" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35" />
          </div>
          <div className="flex items-center gap-2 px-3 h-11 rounded-xl bg-white/[0.05] border border-white/10">
            <Lock size={16} className="text-white/40" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35" />
          </div>

          {error && <p className="text-[12px] text-red-400">{error}</p>}

          <button type="submit" disabled={busy} className="w-full h-12 rounded-xl bg-[#8b5cf6] text-white text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-[#7c4ef0] disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {mode === "login" ? "Sign In" : (isFanJoin ? "Join as a Fan" : "Create Account")}
          </button>
        </form>

        <p className="text-center text-[11px] text-white/30 mt-4">
          Members get live streams, early drops & exclusive content.
        </p>
      </div>
    </div>
  );
}
